import React, { JSX, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState, store } from "./redux/store";
import { fetchAllUsers, listenForAuthChanges } from "./redux/authSlice";
import AuthSwitch from "./AUTH/AuthSwitch";
import VerificationOverview from "./AUTH/VERIFICATION/VerificationOverview";
import VerifyEmail from "./AUTH/VERIFICATION/VerifyEmail";
import AccountSettings from "./PROFILE/AccountSettings";
import EditProfile from "./PROFILE/EditProfile";
import LoginSecurity from "./PROFILE/LoginSecurity";
import Profile from "./PROFILE/Profile";
import ProfileSettings from "./PROFILE/ProfileSettings";
import SettingsPrivacy from "./PROFILE/SettingsPrivacy";
import Navigation from "./NAVIGATION/Navigation";
import Home from "./UNIVERSAL/DASHBOARD/Home";
import Wallet from "./UNIVERSAL/DASHBOARD/Wallet";
import PaymentMethods from "./UNIVERSAL/DASHBOARD/PaymentMethods";
import Cards from "./UNIVERSAL/DASHBOARD/Cards";
import Transfer from "./UNIVERSAL/DASHBOARD/Transfer";
import ReceiveTransfer from "./UNIVERSAL/DASHBOARD/ReceiveTransfer";
import MerchantDashboard from "./UNIVERSAL/MERCHANT/DASHBOARD/MerchantDashboard";
import MerchantOnboarding from "./UNIVERSAL/MERCHANT/DASHBOARD/MerchantOnboarding";
import OrganizationOverview from "./UNIVERSAL/MERCHANT/DASHBOARD/OrganizationOverview";
import MerchantOverview from "./UNIVERSAL/MERCHANT/DASHBOARD/MerchantOverview";
import Waitlist from "./UNIVERSAL/DASHBOARD/Waitlist";
import Checkout from "./UNIVERSAL/MERCHANT/DASHBOARD/Checkout";
import Management from "./UNIVERSAL/MANAGEMENTDASHBOARD/DASHBOARD /Management";
import Converter from "./UNIVERSAL/TOOLS/Converter";

const ProtectedRoute = ({ element }: { element: JSX.Element }) => {
  const { user } = useSelector<RootState, RootState["auth"]>(
    (state: RootState) => state.auth
  );
  return user ? element : <Navigate to="/AuthSwitch" replace />;
};

const AppRoutes = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, authLoaded } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    dispatch(listenForAuthChanges());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAllUsers());
  }, [dispatch]);

  if (!authLoaded) {
    return <div style={{ textAlign: "center", marginTop: 100 }} />;
  }

  return (
    <Routes>

      {/* ── PUBLIC — no login required ───────────────────────────── */}

      {/* Cosmoola hosted checkout — guests land here after redirect  */}
      <Route path="/checkout/pay/:sessionId" element={<Checkout />} />

      <Route path="/" element={<Converter />} />
      <Route path="/AuthSwitch" element={<AuthSwitch />} />
      <Route path="/Home" element={<Home />} />

      {/* ── AUTHENTICATED ────────────────────────────────────────── */}

      <Route path="/VerificationOverview/:userId" element={<VerificationOverview />} />
      <Route path="/VerifyEmail/:userId"           element={<VerifyEmail />} />
      <Route path="/Profile/:userId"               element={<Profile />} />
      <Route path="/editprofile/:userId"           element={<EditProfile />} />
      <Route path="/profilesettings/:userId"       element={<ProfileSettings />} />
      <Route path="/settingsprivacy/:userId"        element={<SettingsPrivacy />} />
      <Route path="/accountsettings/:userId"       element={<AccountSettings />} />
      <Route path="/loginsecurity/:userId"         element={<LoginSecurity />} />

      <Route path="/wallet/:userId"                element={<Wallet />} />
      <Route path="/PaymentMethods/:userId"        element={<PaymentMethods />} />
      <Route path="/Cards/:userId"                 element={<Cards />} />
      <Route path="/Transfer/:userId"              element={<Transfer />} />
      <Route path="/ReceiveTransfer/:userId"       element={<ReceiveTransfer />} />

      <Route path="/merchantoverview/:userId"      element={<MerchantOverview />} />
      <Route path="/merchantonboarding/:userId"    element={<MerchantOnboarding />} />
      <Route path="/organizationoverview/:userId"  element={<OrganizationOverview />} />
      <Route path="/merchantdashboard/:userId"     element={<MerchantDashboard />} />

      <Route path="/management/:userId"     element={<Management />} />

      {/* ── FALLBACK ─────────────────────────────────────────────── */}
      <Route path="*" element={<Navigate to={user ? "/" : "/authswitch"} replace />} />

    </Routes>
  );
};

const App = () => (
  <Provider store={store}>
    <Router>
      <AppRoutes />
    </Router>
  </Provider>
);

export default App;